import React, {useEffect, useState} from 'react'
import {UseFormReturn} from 'react-hook-form'
import {useRouteMatch, useLocation} from 'react-router-dom'
import {Stack, Box, BoxProps, Text} from '../../../vanilla'
import {useAddressFields} from '../../../hooks/forms/useAddressFields'
import {Field} from '../../field'
import {SearchForAddressField} from '../../search-for-address-field'
import {SearchForAddressLineField} from '../../search-for-address-line-field'
import {noop} from '../../../utils/utils'
import {Nullable} from '../../../types/utils'
import {CHECKOUT_SUMMARY_PATH} from '../../../constants'
import {AddressFormFields} from '../../../types/forms'
import {useGlobalStore} from '../../../store/hooks/useStore'

interface AddressFieldsProps extends BoxProps<'div'> {
  form: UseFormReturn<AddressFormFields>
  enableEnterManually?: boolean
  inputGroupProps?: BoxProps<'input' | 'textarea'>
  formContainerPartsProps?: {
    top?: BoxProps<'div'>
    middle?: BoxProps<'div'>
    bottom?: BoxProps<'div'>
  }
  showDeliveryAvailability?: boolean
  showPreferredField?: boolean
  labelForPostcodeSearchField?: Nullable<string>
  onPreferredChange?: (value: boolean) => void
}

export const AddressFields = ({
  form,
  inputGroupProps,
  formContainerPartsProps,
  enableEnterManually,
  showDeliveryAvailability = true,
  labelForPostcodeSearchField = null,
  showPreferredField = true,
  onPreferredChange,
  ...rest
}: AddressFieldsProps) => {
  const {pathname} = useLocation()
  const {getCustomSitePreferenceById, isCustomSitePreferenceSet, customSitePreferences} =
    useGlobalStore()

  // You can control UI of form-parts by passing props to formContainerPartsProps
  const {top, middle, bottom} = formContainerPartsProps || {}
  const [enterManually, setEnterManually] = useState(!enableEnterManually)
  const {isExact: noAddressesHelpText} = useRouteMatch(['/book-delivery']) || {isExact: false}

  const [canEditAddressId, setCanEditAddressId] = useState(false)

  const {isExact: isRegisterPage} = useRouteMatch(['/account/register']) || {isExact: false}
  
  const fields = useAddressFields({
    form,
    withAddressHelpText: !noAddressesHelpText,
    pathname,
  })

  const saveOrPreferredAddressFieldValue = form.watch('preferred') || false
  const isCheckout = pathname === CHECKOUT_SUMMARY_PATH || pathname === '/pre-book-delivery/payment'

  useEffect(() => {
    if (onPreferredChange && typeof onPreferredChange === 'function') {
      onPreferredChange(saveOrPreferredAddressFieldValue)
    }
  }, [saveOrPreferredAddressFieldValue, onPreferredChange])

  // Reset the form so we don't keep any stale data
  useEffect(() => {
    const editAddressIdPref = async () => {
      if (!isCustomSitePreferenceSet('enableAddressIdEdit')) {
        await getCustomSitePreferenceById('enableAddressIdEdit')
      }
      setCanEditAddressId(customSitePreferences['enableAddressIdEdit'] as boolean)
    }
    editAddressIdPref()
    form.reset()
  }, [])

  return (
    <Stack spacing="8px" {...rest}>
      <Stack
        spacing="24px"
        bg="white"
        width="full"
        padding="24px"
        {...(top || {})}
        marginBottom={'24px'}
      >
        <Field {...fields.title} inputGroupProps={inputGroupProps || {}} />
        <Field {...fields.firstName} inputGroupProps={inputGroupProps || {}} />
        <Field {...fields.lastName} inputGroupProps={inputGroupProps || {}} />
        <SearchForAddressField
          form={form}
          showAsterisk={isCheckout}
          showAvailability={showDeliveryAvailability}
          setEnterManually={enableEnterManually ? setEnterManually : noop}
          isEnterManually={enableEnterManually ? enterManually : true}
          inputGroupProps={inputGroupProps || {}}
          label={labelForPostcodeSearchField ?? undefined}
        />
        <Field
          {...fields.addressId}
          inputProps={{
            ...fields.addressId.inputProps,
            ...(!isRegisterPage && !canEditAddressId ? {disabled: true} : {}),
          }}
          inputGroupProps={inputGroupProps || {}}
        />
      </Stack>

      {enableEnterManually && !enterManually ? (
        <Box padding="24px">
          <Text
            variant="text3"
            as="button"
            bg="transparent"
            border="none"
            type="button"
            cursor="pointer"
            textDecoration="underline"
            lineHeight="shorter"
            color={{default: 'gray800', hover: 'gray700'}}
            onClick={() => setEnterManually(true)}
          >
            Manually enter address
          </Text>
        </Box>
      ) : null}
      {enterManually && (
        <Stack
          spacing="24px"
          bg={isCheckout ? 'white' : 'gray50'}
          paddingX="24px"
          paddingY={isCheckout ? '0px' : '24px'}
          {...(middle || {})}
        >
          <SearchForAddressLineField
            showAsterisk={true}
            field={fields.address1}
            form={form}
            inputGroupProps={inputGroupProps || {}}
            isRegisterPage={isRegisterPage}
            canEditAddressId={canEditAddressId}
          />
          <Field {...fields.address2} inputGroupProps={inputGroupProps || {}} />
          <Field {...fields.city} inputGroupProps={inputGroupProps || {}} />
          <Field {...fields.stateCode} inputGroupProps={inputGroupProps || {}} />
          {!isCheckout && <Field {...fields.postcode} inputGroupProps={inputGroupProps || {}} />}
          <Field {...fields.phone} inputGroupProps={inputGroupProps || {}} />
          {isCheckout && <Field {...fields.email} inputGroupProps={inputGroupProps || {}} />}
          <Field {...fields.countryCode} />
        </Stack>
      )}
      {showPreferredField && (
        <Stack bg="white" padding="24px" paddingY="12px" {...(bottom || {})}>
          <Field {...fields.preferred} />
        </Stack>
      )}
    </Stack>
  )
}
